/**
 * Base layout component with navigation, header, registry sidecar, and chat sidebar
 */

import { FileText, LogOut, MessageSquare, PanelLeftOpen, PanelRightOpen, Settings } from "lucide-react";
import { useCallback } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { ChatErrorBoundary } from "@/components/ChatErrorBoundary";
import { ChatMobileSheet } from "@/components/ChatMobileSheet";
import { ChatSidebar } from "@/components/ChatSidebar";
import { RegistrySidecar } from "@/components/RegistrySidecar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/hooks/useAuth";
import { useLayoutState } from "@/hooks/useLayoutState";
import { useRegistrySidecarState } from "@/hooks/useRegistrySidecarState";
import { useListUrlState } from "@/lib/url-state";

export function Layout() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Chat sidebar state
  const {
    sidebarWidth,
    isSidebarOpen,
    isMobileSheetOpen,
    isMobile,
    setSidebarWidth,
    toggleSidebar,
    toggleMobileSheet,
    closeMobileSheet,
    MIN_WIDTH,
    MAX_WIDTH,
  } = useLayoutState();

  // Registry sidecar state
  const {
    sidecarWidth,
    isSidecarOpen,
    isMobile: isSidecarMobile,
    setSidecarWidth,
    toggleSidecar,
    MIN_WIDTH: SIDECAR_MIN_WIDTH,
    MAX_WIDTH: SIDECAR_MAX_WIDTH,
  } = useRegistrySidecarState();

  // URL state for registry selection and document type
  const [urlState, setUrlState] = useListUrlState();
  const selectedRegistryId = urlState.registry;
  const selectedDocumentType = urlState.tab;

  // Handle registry selection - updates URL state
  const handleRegistrySelect = useCallback(
    (registryId: string | null) => {
      setUrlState({ registry: registryId, page: 0 });
    },
    [setUrlState],
  );

  // Handle document type selection - updates URL state
  const handleDocumentTypeSelect = useCallback(
    (type: typeof selectedDocumentType) => {
      setUrlState({ tab: type, page: 0 });
    },
    [setUrlState],
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Registry Sidecar (Left) - Desktop only */}
      {isAuthenticated && !isSidecarMobile && isSidecarOpen && (
        <RegistrySidecar
          width={sidecarWidth}
          onWidthChange={setSidecarWidth}
          minWidth={SIDECAR_MIN_WIDTH}
          maxWidth={SIDECAR_MAX_WIDTH}
          onClose={toggleSidecar}
          selectedRegistryId={selectedRegistryId}
          onRegistrySelect={handleRegistrySelect}
          selectedDocumentType={selectedDocumentType}
          onDocumentTypeSelect={handleDocumentTypeSelect}
        />
      )}

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm dark:bg-gray-800">
          <div className="px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Registry sidecar toggle button (when collapsed or on mobile) - visible on all screen sizes */}
                {isAuthenticated && (!isSidecarOpen || isSidecarMobile) && (
                  <button
                    type="button"
                    onClick={toggleSidecar}
                    className="rounded p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                    aria-label="Open registry sidecar"
                    data-testid="registry-sidecar-toggle"
                  >
                    <PanelLeftOpen className="h-5 w-5" />
                  </button>
                )}
                <div className="flex items-center space-x-2">
                  <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Invoice Viewer</h1>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {isAuthenticated && (
                  <>
                    <nav className="hidden items-center space-x-6 md:flex">
                      <Link
                        to="/invoices"
                        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                      >
                        <FileText className="h-4 w-4" />
                        <span>Invoices</span>
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                      >
                        <Settings className="h-4 w-4" />
                        <span>Settings</span>
                      </Link>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </nav>

                    {/* Chat toggle button */}
                    {isMobile ? (
                      <button
                        type="button"
                        onClick={toggleMobileSheet}
                        className="rounded-lg bg-blue-600 p-2 text-white hover:bg-blue-700"
                        aria-label="Open chat"
                        data-testid="chat-mobile-toggle"
                      >
                        <MessageSquare className="h-5 w-5" />
                      </button>
                    ) : (
                      !isSidebarOpen && (
                        <button
                          type="button"
                          onClick={toggleSidebar}
                          className="rounded p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                          aria-label="Open chat sidebar"
                          data-testid="chat-sidebar-toggle"
                        >
                          <PanelRightOpen className="h-5 w-5" />
                        </button>
                      )
                    )}
                  </>
                )}
                <ThemeToggle />
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Desktop Chat Sidebar */}
      {isAuthenticated && !isMobile && isSidebarOpen && (
        <ChatErrorBoundary>
          <ChatSidebar
            width={sidebarWidth}
            onWidthChange={setSidebarWidth}
            minWidth={MIN_WIDTH}
            maxWidth={MAX_WIDTH}
            onClose={toggleSidebar}
          />
        </ChatErrorBoundary>
      )}

      {/* Mobile Chat Sheet */}
      {isAuthenticated && isMobile && (
        <ChatErrorBoundary>
          <ChatMobileSheet isOpen={isMobileSheetOpen} onClose={closeMobileSheet} />
        </ChatErrorBoundary>
      )}
    </div>
  );
}
